import React, { startTransition, useCallback, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet'
import PageHeader from 'components/PageHeader'
import PageHeaderTitle from 'components/PageHeaderTitle'
import PageHeaderSubtitle from 'components/PageHeaderSubtitle'
import PageHeaderPrimaryButton from 'components/PageHeaderPrimaryButton'
import PageContainer from 'components/PageContainer'
import PageWrapper from 'components/PageWrapper'
import Suspense from 'components/Suspense'
import Condition from 'components/Condition'
import Link from 'components/Link'
import { Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { isClient, isGuest } from 'services/user'
import { graphql, PreloadedQuery, usePreloadedQuery, useQueryLoader } from 'react-relay'
import { useNodes } from 'hooks/relay'
import { Questions_Query } from '__generated__/Questions_Query.graphql'
import { readInlineData } from 'relay-runtime'
import { needsAccountSetup } from 'services/auth'
import { Questions_isAllowed$key } from '__generated__/Questions_isAllowed.graphql'
import { UseQuestionDialog_Query } from '__generated__/UseQuestionDialog_Query.graphql'
import AuthGate from 'components/AuthGate'
import { useResponsiveBreakpoint } from 'hooks/useResponsiveBreakpoint'
import Question from './components/Question'
import UseQuestionDialog, { UseQuestionDialogQuery } from './components/UseQuestionDialog'
import SubmitQuestionDialog from './components/SubmitQuestionDialog'
import useStyles from './styles'

export const QuestionsQuery = graphql`
  query Questions_Query (
    $title: String,
    $hasCategory: Boolean!
    $slug: String!
  ) {
    currentUser {
      ...user_isGuest
      ...user_isClient
      ...Questions_isAllowed
      ...auth_checkAuthForPage
      ...Question_User
      ...AuthGate_User
    }
    
    questions(
      first: 40,
      filters: {
        featured: true,
        title: $title,
      }
    ) @skip(if: $hasCategory) {
      nodes {
        id
        ...Question_Question
      }
    }
    
    questionCategory(slug: $slug) @include(if: $hasCategory) {
      __typename
      ... on Industry {
        name
        featuredQuestions {
          id
          ...Question_Question
        }
      }
      ... on Specialization {
        name
        featuredQuestions {
          id
          ...Question_Question
        }
      }
      ... on Skill {
        name
        featuredQuestions {
          id
          ...Question_Question
        }
      }
      ... on Tag {
        name
        featuredQuestions {
          id
          ...Question_Question
        }
      }
    }
  }
`

export function isAllowed(userFragmentRef: Questions_isAllowed$key) {
  const user = readInlineData(graphql`
    fragment Questions_isAllowed on User @inline {
      ...auth_needsAccountSetup
    }
  `, userFragmentRef)

  // Allow guests, clients and members, but if they require account setup then redirect them to it
  return !needsAccountSetup(user)
}

function Questions({ preloadedQuery }: { preloadedQuery: PreloadedQuery<Questions_Query>}) {
  const { classes } = useStyles()
  const theme = useTheme()
  const data = usePreloadedQuery(QuestionsQuery, preloadedQuery)
  const user = data?.currentUser
  const category = useMemo(() => (
    data?.questionCategory?.__typename === '%other' ? null : data?.questionCategory
  ), [data])

  const isMobile = useResponsiveBreakpoint(theme.breakpoints.values.sm)

  const questionsNodes = useMemo(() => (
    category ? category?.featuredQuestions : data?.questions?.nodes
  ), [data, category])
  const questions = useNodes(questionsNodes)
  const categoryName = category?.name

  const [dialogOpen, setDialogOpen] = useState<'submit' | 'use'>(null)

  const [useQuestionDialogQuery, loadUseQuestionDialog] = useQueryLoader<UseQuestionDialog_Query>(UseQuestionDialogQuery)

  const postAJobLink = useMemo(() => {
    if (isGuest(user)) return '/signup/client?mode=job'
    if (isClient(user)) return '/hire/jobs/new/details'
    return null
  }, [user])

  const handleUseQuestion = useCallback((newQuestionId: string) => {
    startTransition(() => {
      setDialogOpen('use')
      loadUseQuestionDialog({ questionId: newQuestionId }, { fetchPolicy: 'store-and-network' })
    })
  }, [])

  const openSubmitQuestion = useCallback(() => {
    setDialogOpen('submit')
  }, [setDialogOpen])

  const closeDialog = useCallback(() => {
    setDialogOpen(null)
  }, [setDialogOpen])

  const allowed = useMemo(() => isAllowed(user), [user])

  return (
    <AuthGate userFragmentRef={user} allowed={allowed}>
      <Helmet>
        <title>Questions</title>
        <meta name="description" content="Post a job with Structured Interview Questions that correlate highly to successful hiring based on a research from Standford University." />
        <meta property="og:title" content={`${categoryName ? `${categoryName} ` : ''}Questions - Flexhire`} />
        <meta property="og:description" content="Post a job with Structured Interview Questions that correlate highly to successful hiring based on a research from Standford University." />
      </Helmet>
      <PageHeader>
        <PageHeaderTitle variant="center">
          Science Based Successful Hiring
        </PageHeaderTitle>

        <PageHeaderSubtitle variant="center">
          Asking all candidates the same questions during an interview is an approach known as Structured Interview Questions.
          According to research from Standford University based on 65 years of interview data, Structured Interview Questions correlate highly to successful hiring.
          <br /><br />
          Flexhire makes it simple for our customers to ask Structured Interview Questions for open roles and see video responses from candidates.
          Ask your own specific questions to candidates or choose from our pre-existing top {categoryName || 'relevant'} interview questions.
        </PageHeaderSubtitle>

        {postAJobLink && (
          <Link href={postAJobLink} style={{ textDecoration: 'none', maxWidth: 700, display: 'block', margin: '0 auto' }}>
            <PageHeaderPrimaryButton data-cy="post-a-job">
              <Condition condition={!isMobile}>
                Post a Job with{categoryName ? ` ${categoryName} ` : ' '}Structured Interview Questions
              </Condition>

              <Condition condition={isMobile}>
                Post a Job
              </Condition>
            </PageHeaderPrimaryButton>
          </Link>
        )}
      </PageHeader>
      <PageContainer>
        <PageWrapper withoutCard>
          <div>
            {(questions.length === 0) && (
              <div className={classes.placeholder} />
            )}

            <Suspense noSsr>
              {questions.map(q => (
                <div style={{ paddingTop: theme.spacing(3) }} key={q.id}>
                  <Question
                    userFragmentRef={user}
                    questionFragmentRef={q}
                    handleUse={handleUseQuestion}
                  />
                </div>
              ))}
            </Suspense>
          </div>

          <Grid container>
            <Grid item xs={12} md={6}>
              {postAJobLink && (
                <Link href={postAJobLink} style={{ textDecoration: 'none' }}>
                  <PageHeaderPrimaryButton>
                    Post a Job with Questions
                  </PageHeaderPrimaryButton>
                </Link>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <PageHeaderPrimaryButton onClick={openSubmitQuestion} data-cy="submit-question">
                Have a Good Question? Add it!
              </PageHeaderPrimaryButton>
            </Grid>
          </Grid>

          {/*
          // The way this is supposed to work is that if a top level category is picked (eg Technology)
          // then this child component should show "Top Backend Questions", "Top Fullstack Questions" etc
          // Refactor it and re-enable
            {categoryName && (
              <SampleQuestions
                parentName={categoryName}
                title="Related Interview Questions"
                subtitle={null}
              />
            )}
            */}
        </PageWrapper>
      </PageContainer>
      <Suspense noSsr>
        {useQuestionDialogQuery && (
          <UseQuestionDialog
            open={dialogOpen === 'use'}
            preloadedQuery={useQuestionDialogQuery}
            onClose={closeDialog}
          />
        )}
      </Suspense>
      {dialogOpen === 'submit' && (
        // Make sure dialog is unmounted when closed, to reset the form
        (
          <SubmitQuestionDialog
            open={dialogOpen === 'submit'}
            onClose={closeDialog}
          />
        )
      )}
    </AuthGate>
  )
}

export default Questions
